import SearchIcon from "@mui/icons-material/Search";
import React, { useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { dashboardMatchesSearchTerm, getPrimaryCategory, SearchableDashboard } from "../../../constants";
import { useDashboardSearchTracking } from "../../../hooks";
import { useDashboardRegistry } from "../../../hooks/react-query/dashboards/useDashboardRegistry";
import "../../../assets/styles/component/dashboardSearchBar.scss";

const MAX_RESULTS = 8;

export const DashboardSearchBar = () => {
  const history = useHistory();
  const { dashboards } = useDashboardRegistry();
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  const matches = useMemo(() => {
    const trimmed = searchTerm.trim().toLowerCase();
    if (!trimmed) return [];

    const results: { dashboard: SearchableDashboard; matchedTerm?: string }[] = [];
    for (const dashboard of dashboards) {
      // is_active now doubles as "archived" (dashboardsApi.ts's own getDashboardRegistry
      // comment) - excluded from Home's own search the same way the Dashboards page's own search
      // excludes it; findable via the dedicated /archived page instead.
      if (!dashboard.is_active) continue;
      // Matches name, description, or any KPI matched_terms entry (see dashboardMatchesSearchTerm's
      // own comment) - shared with the Dashboards page search bar so both search the same way.
      if (!dashboardMatchesSearchTerm(dashboard, trimmed)) continue;
      const nameMatch = dashboard.name.toLowerCase().includes(trimmed);
      // Each entry is a {term, description, external_ref} record (all nullable), not a bare
      // string - matches against `.term` specifically, and skips any entry with no term at all.
      const matchedTerm = dashboard.matched_terms?.find((entry) => entry.term?.toLowerCase().includes(trimmed))?.term ?? undefined;
      // Only surface "why this matched" when the name itself didn't already explain it (a
      // description-only match has no short snippet worth showing, so it just shows no hint).
      results.push({ dashboard, matchedTerm: nameMatch ? undefined : matchedTerm });
    }
    return results;
  }, [searchTerm, dashboards]);

  const { trackSelection } = useDashboardSearchTracking(searchTerm, matches.length);

  const handleSelect = (dashboard: SearchableDashboard) => {
    // Before the clear below - the hook reads the live query, so clearing first would record an
    // empty one.
    trackSelection(dashboard);
    setSearchTerm("");
    setIsOpen(false);
    // Straight to the dashboard's own page - this used to route through Analysis instead
    // (pre-selecting the dashboard there via a ?dashboardId= querystring), which read as "clicking
    // a dashboard opens the wrong page" since Analysis isn't what a user searching by name expects
    // to land on. Matches DashboardCard's own Open button (dashboard.path), including access
    // control: PrivateRoute still gates the destination itself, so a dashboard the user can't open
    // correctly shows Access Denied rather than this list needing its own has_access branch.
    history.push({ pathname: dashboard.path, search: "" });
  };

  return (
    <div className="dashboard_search_bar" data-test-id="dashboard-search-bar">
      <SearchIcon className="dashboard_search_bar_icon" />
      <input
        type="text"
        value={searchTerm}
        placeholder="Search dashboards or KPIs"
        onChange={(e) => {
          setSearchTerm(e.target.value);
          setIsOpen(true);
        }}
        onFocus={() => setIsOpen(true)}
        onBlur={() => setIsOpen(false)}
        data-test-id="dashboard-search-bar-input"
      />

      {isOpen && matches.length > 0 && (
        <ul className="dashboard_search_bar_results" data-test-id="dashboard-search-bar-results">
          {matches.map(({ dashboard, matchedTerm }) => (
            // onMouseDown (not onClick) fires before the input's onBlur closes the list.
            <li key={dashboard.key} onMouseDown={() => handleSelect(dashboard)} data-test-id={`dashboard-search-result-${dashboard.key}`}>
              <span className="dashboard_search_bar_result_text">
                <span className="dashboard_search_bar_result_name">
                  {dashboard.name} - {getPrimaryCategory(dashboard)}
                </span>
                {matchedTerm && <span className="dashboard_search_bar_result_matched_term">Matched: {matchedTerm}</span>}
              </span>
            </li>
          ))}
        </ul>
      )}

      {/* A search with no matches at all used to just silently render nothing here - the dropdown
          simply never appeared, no different from not having searched yet, with no indication
          whether that meant "still typing" or "genuinely nothing matches". */}
      {isOpen && searchTerm.trim() && matches.length === 0 && (
        <div className="dashboard_search_bar_no_results" data-test-id="dashboard-search-bar-no-results">
          No dashboards or KPIs match &quot;{searchTerm.trim()}&quot;. Try a different search term.
        </div>
      )}
    </div>
  );
};
